import { PaginationMeta } from "@/interfaces/api.interface";
import { clsx } from "clsx";
import { useLocale } from "next-intl";
import { GroupBase } from "react-select";
import type {
  ComponentProps,
  UseAsyncPaginateParams,
} from "react-select-async-paginate";
import { AsyncPaginate } from "react-select-async-paginate";
import type { CreatableProps } from "react-select/creatable";

type DropdownOption = {
  label: string;
  value: string;
};

type AsyncPaginateProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean
> = CreatableProps<OptionType, IsMulti, Group> &
  UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti>;

type AsyncSelectProps = AsyncPaginateProps<
  DropdownOption,
  GroupBase<DropdownOption>,
  PaginationMeta,
  true
>;

const AsyncSelect = ({
  variant,
  ...props
}: AsyncSelectProps & { variant: "small" | "large" }) => {
  const locale = useLocale();
  return (
    <AsyncPaginate
      {...props}
      unstyled
      id="react-select"
      classNames={{
        container() {
          return clsx(
            "relative block w-full h-full rounded-lg cursor-text !h-full",
            "ring-1 ring-input bg-transparent",
            "focus-within:ring-2 focus-within:ring-primary focus-within:outline-none h-max-content"
          );
        },
        control() {
          return clsx(
            "inset-0 flex items-center w-full h-full rounded-lg z-10 cursor-text !h-full",
            "bg-transparent outline-none",
            "placeholder:text-muted-foreground text-gray-800 !overflow-auto",
            "px-4 !py-0",
            {
              "text-lg leading-[22.5px]": variant === "large",
            },

            {
              "text-base leading-5": variant === "small",
            },
            {
              "pl-9": locale === "en",
            },
            {
              "pr-9": locale === "ar",
            }
          );
        },
        input() {
          return "bg-transparent outline-none";
        },
        menu(props) {
          return clsx(
            "border-0 mt-1",
            "isolate w-max rounded-b-lg",
            "overflow-y-auto",
            "bg-white dark:bg-zinc-800/75",
            {
              "p-1 shadow": props.options.length > 0 || props.hasValue,
            }
          );
        },
        valueContainer() {
          return clsx("flex h-full gap-1 !overflow-auto");
        },
        multiValue() {
          return clsx(
            "bg-gray-200 rounded-lg py-[7px] px-[10px] text-gray-900 mr-2 flex items-center gap-1",
            {
              "text-sm": variant == "small",
            },
            {
              "text-sm": variant == "large",
            }
          );
        },
        placeholder() {
          return clsx("text-gray-500 line-clamp-1", {
            "text-lg leading-[22.5px]": variant === "large",
            "text-base leading-5": variant === "small",
          });
        },
        noOptionsMessage() {
          return clsx("text-zinc-500 my-2", "text-base sm:text-sm");
        },
      }}
      menuPlacement="auto"
    />
  );
};

export default AsyncSelect;
